import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Switch } from "@akuna/ui";
import { cn } from "@/lib/utils";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import {
  useDaycareCapacity,
  useDaycareOccupancyToday,
  useSaveDaycareCapacity,
  type DaycareCapacity,
} from "@/hooks/useDaycareCapacity";
import { SectionCard, SectionHeader, Stepper } from "./primitives";
import { getDisplayedCapacity, type DaycareCapacityDraft as Draft } from "./daycareCapacityDraft";

const SIZES = [
  { key: "small", dot: "bg-akuna-blue", labelKey: "capacity.sizeSmall" },
  { key: "medium", dot: "bg-akuna-amber", labelKey: "capacity.sizeMedium" },
  { key: "large", dot: "bg-akuna-purple", labelKey: "capacity.sizeLarge" },
] as const;

const toDraft = (capacity: DaycareCapacity): Draft => ({
  totalCapacity: capacity.totalCapacity ?? 0,
  limitBySize: capacity.limitBySize,
  capacitySmall: capacity.capacitySmall ?? 0,
  capacityMedium: capacity.capacityMedium ?? 0,
  capacityLarge: capacity.capacityLarge ?? 0,
});

export default function DaycareCapacitySection({ canEdit }: { canEdit: boolean }) {
  const { t } = useTranslation("daycareHotel");
  const { data: capacity, isLoading } = useDaycareCapacity();
  const { data: occupancy } = useDaycareOccupancyToday();
  const save = useSaveDaycareCapacity();

  const [draft, setDraft] = useState<Draft | null>(null);
  const current = draft ?? (capacity ? toDraft(capacity) : null);

  // Steppers fire per click; debounce so a run of clicks lands as one write.
  const debounced = useDebouncedValue(draft, 600);
  const lastSaved = useRef<string | null>(null);
  const saveRef = useRef(save);
  saveRef.current = save;

  useEffect(() => {
    if (!debounced) return;
    const serialized = JSON.stringify(debounced);
    if (lastSaved.current === serialized) return;
    lastSaved.current = serialized;
    saveRef.current.mutate(debounced);
  }, [debounced]);

  if (isLoading || !current) {
    return (
      <section>
        <SectionHeader
          tone="daycare"
          badge={t("badgeDaycare")}
          title={t("capacity.title")}
          description={t("capacity.description")}
        />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[0, 1, 2, 3].map((index) => (
            <SectionCard key={index} className="h-[132px] animate-pulse bg-muted/40" />
          ))}
        </div>
      </section>
    );
  }

  const displayedCapacity = getDisplayedCapacity(current);
  const update = (patch: Partial<Draft>) => setDraft({ ...current, ...patch });

  return (
    <section>
      <SectionHeader
        tone="daycare"
        badge={t("badgeDaycare")}
        title={t("capacity.title")}
        hint={
          occupancy !== undefined && displayedCapacity > 0
            ? t("capacity.usageToday", { used: occupancy, total: displayedCapacity })
            : undefined
        }
        description={t("capacity.description")}
        action={
          <Switch
            checked={current.limitBySize}
            onCheckedChange={(checked) => update({ limitBySize: checked })}
            label={t("capacity.limitBySize")}
            disabled={!canEdit}
          />
        }
      />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <SectionCard className="p-4">
          <div className="mb-2.5 text-[12.5px] text-muted-foreground">{t("capacity.maxPerDay")}</div>
          <Stepper
            value={displayedCapacity}
            onChange={(next) => update({ totalCapacity: next })}
            decreaseLabel={t("capacity.decrease", { label: t("capacity.maxPerDay") })}
            increaseLabel={t("capacity.increase", { label: t("capacity.maxPerDay") })}
            disabled={!canEdit || current.limitBySize}
          />
          <p className="mt-2.5 text-xs text-color-light">{t("capacity.maxPerDayHint")}</p>
        </SectionCard>

        {SIZES.map((size) => {
          const field = `capacity${size.key[0].toUpperCase()}${size.key.slice(1)}` as
            | "capacitySmall"
            | "capacityMedium"
            | "capacityLarge";
          const label = t(size.labelKey);
          return (
            <SectionCard
              key={size.key}
              className={cn("p-4 transition-opacity", !current.limitBySize && "opacity-50")}
            >
              <div className="mb-2.5 flex items-center gap-1.5 text-[12.5px] text-muted-foreground">
                <span className={cn("h-2.5 w-2.5 rounded-full", size.dot)} aria-hidden="true" />
                {label}
              </div>
              <Stepper
                value={current[field]}
                onChange={(next) => update({ [field]: next } as Partial<Draft>)}
                decreaseLabel={t("capacity.decrease", { label })}
                increaseLabel={t("capacity.increase", { label })}
                disabled={!canEdit || !current.limitBySize}
              />
              <p className="mt-2.5 text-xs text-color-light">
                {current.limitBySize ? t("capacity.sizeHint") : t("capacity.sizeDisabledHint")}
              </p>
            </SectionCard>
          );
        })}
      </div>
    </section>
  );
}
